import React, { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import Calling from '../../api/Calling';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import {
    FaCoins,
    FaEye,
    FaEdit,
    FaTrashAlt,
    FaClock,
    FaCalendarAlt,
    FaQuestionCircle,
    FaChartBar,
    FaHeart,
    FaRedo,
    FaBolt,
    FaPlay,
    FaStop,
    FaTrophy,
    FaFilter,
    FaSearch,
    FaStar,
    FaGem,
    FaFire,
    FaSyncAlt,
    FaUserClock,
    FaCheck
} from 'react-icons/fa';
import {
    MdDashboard,
    MdSchedule,
    MdLiveTv,
    MdTimer,
    MdAnalytics,
    MdMoreVert,
    MdRefresh
} from 'react-icons/md';
import {
    HiSparkles,
    HiLightningBolt,
    HiTrendingUp
} from 'react-icons/hi';
import {
    IoStatsChart,
    IoTime,
    IoTrophy
} from 'react-icons/io5';

const QuizShow = () => {
    const [loader, setLoader] = useState(false);
    const [quizzes, setQuizzes] = useState([]);
    const [filterType, setFilterType] = useState('daily');
    const [currentTime, setCurrentTime] = useState(new Date());
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const refreshTimeoutRef = useRef({});

    const fetchData = async () => {
        try {
            setLoader(true);
            const res = await Calling.getQuiz(filterType);
            if (res.status) {
                setQuizzes(res.data || []);
            } else {
                console.error('Error in response:', res.message);
                setQuizzes([]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setQuizzes([]);
        } finally {
            setLoader(false);
        }
    };

    // Auto refresh data when schedule time changes or session ends
    const scheduleAutoRefresh = (quiz) => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const nowIST = new Date(now.getTime() + istOffset);
        const currentTime = nowIST.getTime();

        const scheduleTime = new Date(quiz.scheduleTime);
        const startTime = scheduleTime.getTime();
        const endTime = startTime + (quiz.quizTiming * 60 * 1000);
        const resultEndTime = endTime + (quiz.resultTime || 10) * 60 * 1000; // Default 10 minutes for results

        // Clear existing timeout for this quiz
        if (refreshTimeoutRef.current[quiz._id]) {
            clearTimeout(refreshTimeoutRef.current[quiz._id]);
        }

        let refreshAt = null;

        if (currentTime < startTime) {
            // Refresh when quiz starts
            refreshAt = startTime - currentTime;
        } else if (currentTime >= startTime && currentTime <= endTime) {
            // Refresh when quiz ends
            refreshAt = endTime - currentTime;
        } else if (currentTime > endTime && currentTime <= resultEndTime) {
            // Refresh when result period ends
            refreshAt = resultEndTime - currentTime;
        }

        if (refreshAt && refreshAt > 0) {
            refreshTimeoutRef.current[quiz._id] = setTimeout(() => {
                console.log(`Auto refreshing data for quiz: ${quiz.title}`);
                fetchData();
            }, refreshAt + 1000); // Add 1 second buffer
        }
    };

    useEffect(() => {
        fetchData();

        // Schedule auto refresh for all quizzes
        quizzes.forEach(quiz => {
            if (quiz.type === 'live') {
                scheduleAutoRefresh(quiz);
            }
        });

        intervalRef.current = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            // Clear all refresh timeouts
            Object.values(refreshTimeoutRef.current).forEach(timeout => {
                clearTimeout(timeout);
            });
        };
    }, [filterType]);

    // Re-schedule auto refresh when quizzes change
    useEffect(() => {
        quizzes.forEach(quiz => {
            if (quiz.type === 'live') {
                scheduleAutoRefresh(quiz);
            }
        });
    }, [quizzes]);

    const getQuizStatus = (quiz) => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const nowIST = new Date(now.getTime() + istOffset);
        const currentTime = nowIST.getTime();

        if (quiz.type === 'daily') {
            return {
                status: 'daily',
                text: 'Available',
                phase: 'active'
            };
        }

        const scheduleTime = new Date(quiz.scheduleTime);
        const startTime = scheduleTime.getTime();
        const endTime = startTime + (quiz.quizTiming * 60 * 1000);
        const resultTime = quiz.resultTiming || 10; // Default 10 minutes for results
        const resultEndTime = endTime + (resultTime * 60 * 1000);

        if (currentTime < startTime) {
            // Before quiz starts
            const distance = startTime - currentTime;
            const hours = Math.floor(distance / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);
            return {
                status: 'upcoming',
                text: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
                phase: 'scheduled'
            };
        } else if (currentTime >= startTime && currentTime <= endTime) {
            // Quiz is live
            const remaining = endTime - currentTime;
            const minutes = Math.floor(remaining / (1000 * 60));
            const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
            return {
                status: 'live',
                text: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
                phase: 'active'
            };
        } else if (currentTime > endTime && currentTime <= resultEndTime) {
            // Result period
            const remaining = resultEndTime - currentTime;
            const minutes = Math.floor(remaining / (1000 * 60));
            const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
            return {
                status: 'result',
                text: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
                phase: 'result'
            };
        } else {
            // Quiz completely ended
            return {
                status: 'ended',
                text: 'Completed',
                phase: 'completed'
            };
        }
    };

    const getStatusIcon = (phase) => {
        switch (phase) {
            case 'scheduled':
                return <MdSchedule className="w-5 h-5" />;
            case 'active':
                return <MdLiveTv className="w-5 h-5 animate-pulse" />;
            case 'result':
                return <IoStatsChart className="w-5 h-5" />;
            case 'completed':
                return <IoTrophy className="w-5 h-5" />;
            default:
                return <FaQuestionCircle className="w-5 h-5" />;
        }
    };

    const getStatusStyle = (phase) => {
        switch (phase) {
            case 'scheduled':
                return 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 text-blue-700';
            case 'active':
                return 'bg-gradient-to-r from-green-50 to-green-100 border-green-200 text-green-700';
            case 'result':
                return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 text-orange-700';
            case 'completed':
                return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200 text-gray-700';
            default:
                return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200 text-gray-700';
        }
    };

    // Handle view quiz details
    const handleViewQuiz = (id) => {
        navigate(`/manage/quiz/view/${id}`);
    };

    // Handle edit quiz
    const handleEditQuiz = (id) => {
        navigate(`/manage/quiz/add?id=${id}`);
    };

    const handleLeaderBordQuiz = (id, type) => {
        navigate(`/manage/quiz/leaderboard/${id}?type=${type}`);
    };

    // Handle delete quiz
    const handleDeleteQuiz = async (id) => {
        const content = `You are about to delete a quiz. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                const res = await Calling.deleteQuiz({ id: [id] });
                if (res.status) {
                    toast.success('Quiz deleted successfully');
                    fetchData();
                } else {
                    toast.error(res.message || 'Failed to delete quiz');
                }
            } catch (error) {
                console.error('Error deleting quiz:', error);
                toast.error('An error occurred while deleting the quiz');
            } finally {
                setLoader(false);
            }
        }
    };
    const toCap = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();


    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'quizshow'} />
                <hr className='border-main-border' />

                {/* Stats Header */}
                {quizzes.length !== 0 && <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-500 rounded-xl">
                                <MdAnalytics className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-blue-600 font-semibold">Total Quizzes</p>
                                <p className="text-2xl font-bold text-blue-800">{quizzes?.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-green-500 rounded-xl">
                                <FaPlay className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-green-600 font-semibold">Active Quizzes</p>
                                <p className="text-2xl font-bold text-green-800">
                                    {quizzes.filter(q => getQuizStatus(q).phase === 'active').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-500 rounded-xl">
                                <MdSchedule className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-purple-600 font-semibold">Scheduled</p>
                                <p className="text-2xl font-bold text-purple-800">
                                    {quizzes.filter(q => getQuizStatus(q).phase === 'scheduled').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>}

                {/* Filter Controls */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <FaFilter className="w-5 h-5 text-slate-600" />
                            <label htmlFor="quizType" className="font-semibold text-slate-700">Filter by Type:</label>
                            <select
                                id="quizType"
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                                className="px-4 py-2 border-2 border-slate-200 rounded-xl bg-white focus:border-indigo-500 focus:outline-none transition-all duration-300"
                            >
                                <option value="all">All Quizzes</option>
                                <option value="daily">Daily Quizzes</option>
                                <option value="live">Live Quizzes</option>
                            </select>
                        </div>
                    </div>

                    <button
                        onClick={fetchData}
                        disabled={loader}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FaSyncAlt className={`w-5 h-5 ${loader ? 'animate-spin' : ''}`} />
                        <span className="font-semibold">{loader ? 'Refreshing...' : 'Refresh Data'}</span>
                    </button>
                </div>

                {/* Loading State */}
                {loader ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-center">
                            <div className="animate-spin w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full mx-auto mb-6"></div>
                            <p className="text-slate-600 text-lg font-medium">Loading quizzes...</p>
                        </div>
                    </div>
                ) : (
                    <div className="p-4">
                        {quizzes.length === 0 ? (
                            <div className="text-center py-20">
                                <FaSearch className="w-20 h-20 mx-auto mb-6 text-slate-400" />
                                <h3 className="text-2xl font-bold text-slate-600 mb-2">No Quizzes Found</h3>
                                <p className="text-slate-500">Try adjusting your filter or create a new quiz to get started.</p>
                            </div>
                        ) : (
                            <>
                                {/* Quiz Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {quizzes.map(quiz => {
                                        const quizStatus = getQuizStatus(quiz);
                                        return (
                                            <div key={quiz._id} className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-200">
                                                {/* Image Section */}
                                                <div className="relative h-56 overflow-hidden">
                                                    <img
                                                        src={quiz.image}
                                                        alt={quiz.title}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                                                    {/* Enhanced Type Badge with Different Designs */}
                                                    <div className="absolute top-4 right-4">
                                                        {quiz.type === 'daily' ? (
                                                            <div className="relative">
                                                                <div className="bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 px-4 py-2 rounded-lg text-white font-bold text-sm shadow-lg border-2 border-white/20 backdrop-blur-sm">
                                                                    <span className="flex items-center gap-2">
                                                                        <div className="p-1 bg-white/20 rounded-md">
                                                                            <FaCalendarAlt className="w-3 h-3" />
                                                                        </div>
                                                                        Daily Quest
                                                                    </span>
                                                                </div>
                                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-md animate-pulse"></div>
                                                            </div>
                                                        ) : (
                                                            <div className="relative">
                                                                <div className="bg-gradient-to-r from-red-500 via-red-600 to-pink-600 px-4 py-2 rounded-md text-white font-bold text-sm shadow-lg border-2 border-white/30 backdrop-blur-sm transform rotate-1">
                                                                    <span className="flex items-center gap-2 -rotate-1">
                                                                        <div className="p-1 bg-white/25 rounded-sm animate-pulse">
                                                                            <FaBolt className="w-3 h-3" />
                                                                        </div>
                                                                        {toCap(quiz.status)}
                                                                    </span>
                                                                </div>
                                                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-sm animate-ping"></div>
                                                                <div className="absolute top-0 right-0 w-2 h-2 bg-red-300 rounded-sm"></div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Status Icon */}
                                                    <div className="absolute top-4 left-4">
                                                        <div className={`p-2 rounded-full backdrop-blur-sm ${getStatusStyle(quizStatus.phase)}`}>
                                                            {getStatusIcon(quizStatus.phase)}
                                                        </div>
                                                    </div>

                                                    {/* Quiz Status Overlay - Only show for Live quizzes */}
                                                    {quiz.type === 'live' && (
                                                        <div className="absolute bottom-4 left-4 right-4">
                                                            <div className={`p-3 rounded-xl border-2 backdrop-blur-sm ${getStatusStyle(quizStatus.phase)}`}>
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-2">
                                                                        {getStatusIcon(quizStatus.phase)}
                                                                        <span className="font-bold text-sm">
                                                                            {quizStatus.phase === 'pending' && 'Starts in'}
                                                                            {quizStatus.phase === 'scheduled' && 'Starts in'}
                                                                            {quizStatus.phase === 'active' && 'Live ends in'}
                                                                            {quizStatus.phase === 'result' && 'Results for'}
                                                                            {quizStatus.phase === 'completed' && 'Finished'}
                                                                        </span>
                                                                    </div>
                                                                    <span className="font-mono font-bold">
                                                                        {quizStatus.text}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Daily quiz badge */}
                                                    {quiz.type === 'daily' && (
                                                        <div className="absolute bottom-4 left-4 right-4">
                                                            <div className="p-3 rounded-xl border-2 backdrop-blur-sm bg-gradient-to-r from-green-50 to-green-100 border-green-200 text-green-700">
                                                                <div className="flex items-center justify-center gap-2">
                                                                    <FaCheck className="w-4 h-4" />
                                                                    <span className="font-bold text-sm">Always Available</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Content Section */}
                                                <div className="p-6">
                                                    <h3 className="text-xl font-bold mb-3 text-slate-800 line-clamp-1">{quiz.title}</h3>
                                                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{quiz.description}</p>

                                                    {/* Stats Grid */}
                                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                                        <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                                                            <FaCoins className="w-4 h-4 text-yellow-600" />
                                                            <span className="text-sm font-semibold text-yellow-800">{quiz.totalCoin} coins</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                                                            <FaQuestionCircle className="w-4 h-4 text-blue-600" />
                                                            <span className="text-sm font-semibold text-blue-800">{quiz.totalQuestion} Q</span>
                                                        </div>
                                                        {quiz.type === 'daily' ? (
                                                            <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                                                                <FaUserClock className="w-4 h-4 text-purple-600" />
                                                                <span className="text-sm font-semibold text-purple-800">
                                                                    {quiz.playLimit || 1} {quiz.playLimit === 1 ? 'attempt' : 'attempts'}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                                                                <MdTimer className="w-4 h-4 text-green-600" />
                                                                <span className="text-sm font-semibold text-green-800">{quiz.quizTiming}m</span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                                            <FaHeart className="w-4 h-4 text-red-600" />
                                                            <span className="text-sm font-semibold text-red-800">{quiz.lifeLine || 0} lifelines</span>
                                                        </div>
                                                    </div>

                                                    {/* Status and Date */}
                                                    <div className="flex justify-between items-center mb-4">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${quiz.status === 'active' ? 'bg-green-100 text-green-800' :
                                                            quiz.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                            {quiz.status}
                                                        </span>
                                                        <span className="text-xs text-slate-500 font-medium">
                                                            {new Date(quiz.createdAt).toLocaleDateString()}
                                                        </span>
                                                    </div>

                                                    {/* Schedule Info for Live Quizzes */}
                                                    {quiz.type === 'live' && (
                                                        <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                                                <MdSchedule className="w-4 h-4" />
                                                                <span className="font-medium">Scheduled:</span>
                                                            </div>
                                                            <p className="text-sm font-semibold text-slate-800 mt-1">
                                                                {new Date(quiz.scheduleTime).toLocaleString('en-US', {
                                                                    timeZone: 'UTC',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    year: 'numeric',
                                                                    hour: 'numeric',
                                                                    minute: '2-digit',
                                                                    hour12: true
                                                                })}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {/* Action Buttons - Different for Daily vs Live Quizzes */}
                                                    <div className={`flex justify-between items-center gap-2 ${quiz.type === 'daily' ? 'grid-cols-3' : 'grid-cols-4'}`}>
                                                        <button
                                                            onClick={() => handleViewQuiz(quiz._id)}
                                                            className="flex items-center justify-center p-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl flex-1"
                                                            title="View Quiz Details"
                                                        >
                                                            <FaEye className="w-4 h-4" />
                                                        </button>

                                                        <button
                                                            onClick={() => handleEditQuiz(quiz._id)}
                                                            className="flex items-center justify-center p-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                                                            title="Edit Quiz"
                                                        >
                                                            <FaEdit className="w-4 h-4" />
                                                        </button>

                                                        {/* Show leaderboard button only for Live quizzes */}
                                                        {quiz.type === 'live' && (
                                                            <button
                                                                onClick={() => handleLeaderBordQuiz(quiz._id, quiz?.type)}
                                                                className="flex items-center justify-center p-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                                                                title="View Leaderboard"
                                                            >
                                                                <FaTrophy className="w-4 h-4" />
                                                            </button>
                                                        )}

                                                        <button
                                                            onClick={() => handleDeleteQuiz(quiz._id)}
                                                            disabled={quiz?.type === 'live' && (quizStatus.phase === 'active' || quizStatus.phase === 'result')}
                                                            className="flex items-center justify-center p-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                                                            title="Delete Quiz"
                                                        >
                                                            <FaTrashAlt className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizShow;